import { hasLowerLevel, mapInfo, worldToRadar, type Level, type MapId } from "@69ly/maps";
import {
  emptyPhases,
  MAX_STRAT_ITEMS,
  MAX_STRAT_PHASES,
  MAX_PEN_POINTS,
  STRAT_COLORS,
  STRAT_SNAP,
  STRAT_WIDTHS,
  type Grenade,
  type Side,
  type Strat,
  type StratColor,
  type StratItem,
  type StratPhase,
  type StratWidth,
  type Stuff,
} from "@69ly/shared";
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router";
import { GrenadeGlyph } from "../components/GrenadeGlyph.tsx";
import { Icon } from "../components/Icon.tsx";
import { AtPoint, Radar, useRadarView } from "../components/Radar.tsx";
import { DraftSvg, exportBoardPng, GRENADE_COLOR, S, SIDE_COLOR, STRAT_COLOR, STRAT_WIDTH, StratItemSvg, TOKEN_R, widthOf } from "../components/StratCanvas.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { StuffLink } from "../components/StuffDrawer.tsx";
import { api, ApiFailure } from "../lib/api.ts";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { radarUrl } from "../lib/radars.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { useCreateStrat, useDeleteStrat, useReloadStrat, useSaveStrat, useStrat, useStrats } from "../lib/strat-queries.ts";
import { MapBar, stuffLevel, useMapParam } from "./MapView.tsx";

type Tool = "select" | "token" | "arrow" | "pen" | "text" | Grenade | "eraser";
const GRENADE_TOOLS: Grenade[] = ["smoke", "flash", "molotov", "he"];
const isGrenadeTool = (t: Tool): t is Grenade => (GRENADE_TOOLS as string[]).includes(t);

type Draft = { kind: "arrow"; from: [number, number]; to: [number, number] } | { kind: "pen"; points: [number, number][] };

let idCounter = 0;
const newId = () => `${Date.now().toString(36)}${(idCounter++).toString(36)}`.slice(-12);
const r4 = (n: number) => Math.round(n * 10000) / 10000;
const clampFrac = (n: number) => Math.min(1.2, Math.max(-0.2, n));

/** A long stroke keeps its shape with fewer points: every other point until it fits. */
function thin(points: [number, number][]): [number, number][] {
  let out = points;
  while (out.length > MAX_PEN_POINTS) out = out.filter((_, i) => i % 2 === 0 || i === out.length - 1);
  return out;
}

/** Moves an item by a distance in radar fractions. */
function moved(item: StratItem, du: number, dv: number): StratItem {
  const p = (q: [number, number]): [number, number] => [r4(clampFrac(q[0] + du)), r4(clampFrac(q[1] + dv))];
  switch (item.kind) {
    case "arrow":
      return { ...item, from: p(item.from), to: p(item.to) };
    case "pen":
      return { ...item, points: item.points.map(p) };
    case "token":
      // Moved with where they look, so they keep facing the same way.
      return { ...item, u: r4(clampFrac(item.u + du)), v: r4(clampFrac(item.v + dv)), look: item.look ? p(item.look) : null };
    case "grenade":
      // Moved off its landing: no longer that stuff.
      return { ...item, u: r4(clampFrac(item.u + du)), v: r4(clampFrac(item.v + dv)), stuffId: null };
    default:
      return { ...item, u: r4(clampFrac(item.u + du)), v: r4(clampFrac(item.v + dv)) };
  }
}

// ---------------------------------------------------------------- The board

/** Players' view lines shown or hidden, remembered in this browser. */
function useShowViews(): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState(() => {
    try {
      return window.localStorage.getItem("69ly.strat.views") !== "off";
    } catch {
      return true;
    }
  });
  const set = (v: boolean) => {
    setValue(v);
    try {
      window.localStorage.setItem("69ly.strat.views", v ? "on" : "off");
    } catch {
      // No storage: for this visit only.
    }
  };
  return [value, set];
}

function ViewLinesToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="btn btn-quiet btn-small" aria-pressed={value} onClick={() => onChange(!value)} title={t("strat.viewLinesHelp")}>
      <Icon name={value ? "eye" : "eyeOff"} size={14} />
      {t("strat.viewLines")}
    </button>
  );
}

interface Doc {
  title: string;
  side: Side;
  notes: string;
  phases: StratPhase[];
}

type SaveState = "saved" | "dirty" | "saving" | "error" | "conflict" | "invalid";

function StratEditor({ strat, map, stuffs }: { strat: Strat; map: MapId; stuffs: Stuff[] }) {
  const { t } = useTranslation();
  const editable = strat.canEdit;
  const info = mapInfo(map);
  const [doc, setDoc] = useState<Doc>({ title: strat.title, side: strat.side, notes: strat.notes, phases: strat.phases });
  const [past, setPast] = useState<StratPhase[][]>([]);
  const [future, setFuture] = useState<StratPhase[][]>([]);
  const [pi, setPi] = useState(0);
  const [tool, setTool] = useState<Tool>(editable ? "token" : "select");
  const [color, setColor] = useState<StratColor>("yellow");
  const [width, setWidth] = useState<StratWidth>("normal");
  const [showViews, setShowViews] = useShowViews();
  const [tokenSide, setTokenSide] = useState<Side>(strat.side);
  const [level, setLevel] = useState<Level>("upper");
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [textDraft, setTextDraft] = useState<{ u: number; v: number; value: string; editing: string | null } | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [presenting, setPresenting] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // Saving: a little after the last change; the server refuses a save based on an older copy.
  const [base, setBase] = useState(strat.updatedAt);
  const [state, setState] = useState<SaveState>("saved");
  const version = useRef(0);
  const save = useSaveStrat(strat.id);
  const reload = useReloadStrat();

  const phases = doc.phases;
  const phase = phases[Math.min(pi, phases.length - 1)];
  const items = phase.items.filter((i) => i.level === level);
  const selectedItem = phase.items.find((i) => i.id === selected) ?? null;
  const linked = useMemo(() => new Map(stuffs.map((s) => [s.id, s])), [stuffs]);

  const touch = () => {
    version.current++;
    setState("dirty");
  };
  const commitPhases = (next: StratPhase[], before: StratPhase[] = phases) => {
    setPast((p) => [...p.slice(-49), before]);
    setFuture([]);
    setDoc((d) => ({ ...d, phases: next }));
    touch();
  };
  const setItems = (fn: (items: StratItem[]) => StratItem[], before?: StratPhase[]) =>
    commitPhases(
      phases.map((p, i) => (i === pi ? { ...p, items: fn(p.items) } : p)),
      before,
    );
  const addItem = (item: StratItem) => {
    if (phase.items.length >= MAX_STRAT_ITEMS) return setNote(t("strat.fullPhase", { max: MAX_STRAT_ITEMS }));
    setItems((xs) => [...xs, item]);
  };
  const removeItem = (id: string) => {
    setItems((xs) => xs.filter((x) => x.id !== id));
    if (selected === id) setSelected(null);
  };
  const undo = () => {
    if (!past.length) return;
    const prev = past[past.length - 1];
    setPast(past.slice(0, -1));
    setFuture([phases, ...future]);
    setDoc((d) => ({ ...d, phases: prev }));
    setPi((i) => Math.min(i, prev.length - 1));
    touch();
  };
  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setPast([...past, phases]);
    setFuture(future.slice(1));
    setDoc((d) => ({ ...d, phases: next }));
    setPi((i) => Math.min(i, next.length - 1));
    touch();
  };

  const docValid = doc.title.trim().length >= 2;
  const doSave = useCallback(
    (overrideBase?: string) => {
      if (!editable) return;
      if (doc.title.trim().length < 2) return setState("invalid");
      const at = version.current;
      setState("saving");
      save.mutate(
        { map, side: doc.side, title: doc.title.trim(), notes: doc.notes, phases: doc.phases, baseUpdatedAt: overrideBase ?? base },
        {
          onSuccess: (s) => {
            setBase(s.updatedAt);
            setState(version.current === at ? "saved" : "dirty");
          },
          onError: (err) => setState(err instanceof ApiFailure && err.code === "edit_conflict" ? "conflict" : "error"),
        },
      );
    },
    [editable, doc, base, map, save],
  );

  useEffect(() => {
    if (state !== "dirty" || !editable) return;
    const timer = setTimeout(() => doSave(), 900);
    return () => clearTimeout(timer);
  }, [state, doc, editable, doSave]);

  // Unsaved drawings when leaving: the browser asks first.
  useEffect(() => {
    if (state === "saved") return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [state]);

  const loadTheirs = async () => {
    const s = await reload(strat.id);
    setDoc({ title: s.title, side: s.side, notes: s.notes, phases: s.phases });
    setBase(s.updatedAt);
    setPast([]);
    setFuture([]);
    setPi(0);
    setState("saved");
  };
  const keepMine = async () => {
    const s = await reload(strat.id);
    doSave(s.updatedAt);
  };

  // Keys: undo, redo, delete the selection, leave a tool.
  useEffect(() => {
    if (!editable) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest("input, textarea, select, [contenteditable]");
      if (typing) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (mod && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selected) {
        e.preventDefault();
        removeItem(selected);
      } else if (e.key === "Escape") {
        setSelected(null);
        setDraft(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // ---- Pointer on the board

  const frac = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = svgRef.current!.getBoundingClientRect();
    return [r4((e.clientX - r.left) / r.width), r4((e.clientY - r.top) / r.height)];
  };
  const gesture = useRef<
    | { kind: "move"; id: string; start: [number, number]; before: StratPhase[]; moved: boolean }
    | { kind: "erase"; before: StratPhase[]; erased: Set<string> }
    | { kind: "aim"; id: string; before: StratPhase[]; moved: boolean }
    | null
  >(null);
  /** The click that ends an aim drag must not also place something. */
  const skipClick = useRef(false);

  /** Saved landings of this grenade on this map and level, on the radar. */
  const landings = useMemo(
    () =>
      isGrenadeTool(tool)
        ? stuffs.filter((s) => s.grenade === tool && stuffLevel(map, s) === level).map((s) => ({ s, p: worldToRadar(map, s.landing.x, s.landing.y) }))
        : [],
    [tool, stuffs, map, level],
  );

  const itemIdAt = (e: { clientX: number; clientY: number }) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const g = el?.closest<SVGGElement>(".strat-current [data-id]");
    return g?.dataset.id ?? null;
  };

  const onDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const at = frac(e);
    // The selected player's aim handle, whatever the tool.
    const aimId = editable ? (document.elementFromPoint(e.clientX, e.clientY)?.closest<SVGGElement>("[data-look]")?.dataset.look ?? null) : null;
    if (aimId) {
      e.stopPropagation();
      gesture.current = { kind: "aim", id: aimId, before: phases, moved: false };
      skipClick.current = true;
      svgRef.current?.setPointerCapture(e.pointerId);
      return;
    }
    const hit = itemIdAt(e);
    if (!editable || tool === "select") {
      if (hit) {
        e.stopPropagation();
        setSelected(hit);
        if (editable) {
          gesture.current = { kind: "move", id: hit, start: at, before: phases, moved: false };
          svgRef.current?.setPointerCapture(e.pointerId);
        }
      } else {
        // Empty map: let the radar pan.
        setSelected(null);
      }
      return;
    }
    e.stopPropagation();
    setSelected(null);
    if (tool === "eraser") {
      gesture.current = { kind: "erase", before: phases, erased: new Set() };
      svgRef.current?.setPointerCapture(e.pointerId);
      if (hit) eraseHit(hit);
      return;
    }
    if (tool === "arrow") {
      setDraft({ kind: "arrow", from: at, to: at });
      svgRef.current?.setPointerCapture(e.pointerId);
      return;
    }
    if (tool === "pen") {
      setDraft({ kind: "pen", points: [at] });
      svgRef.current?.setPointerCapture(e.pointerId);
      return;
    }
  };

  const eraseHit = (id: string) => {
    const g = gesture.current;
    if (g?.kind !== "erase" || g.erased.has(id)) return;
    g.erased.add(id);
    setDoc((d) => ({ ...d, phases: d.phases.map((p, i) => (i === pi ? { ...p, items: p.items.filter((x) => x.id !== id) } : p)) }));
  };

  const onMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    const g = gesture.current;
    if (g?.kind === "aim") {
      const [u, v] = frac(e);
      const look: [number, number] = [r4(clampFrac(u)), r4(clampFrac(v))];
      g.moved = true;
      setDoc((d) => ({ ...d, phases: d.phases.map((p, i) => (i === pi ? { ...p, items: p.items.map((x) => (x.id === g.id && x.kind === "token" ? { ...x, look } : x)) } : p)) }));
      return;
    }
    if (g?.kind === "move") {
      const at = frac(e);
      const du = at[0] - g.start[0];
      const dv = at[1] - g.start[1];
      if (!g.moved && Math.hypot(du, dv) < 0.004) return;
      g.moved = true;
      const original = g.before[pi].items.find((x) => x.id === g.id);
      if (!original) return;
      setDoc((d) => ({ ...d, phases: d.phases.map((p, i) => (i === pi ? { ...p, items: p.items.map((x) => (x.id === g.id ? moved(original, du, dv) : x)) } : p)) }));
      return;
    }
    if (g?.kind === "erase") {
      const hit = itemIdAt(e);
      if (hit) eraseHit(hit);
      return;
    }
    if (!draft) return;
    const at = frac(e);
    if (draft.kind === "arrow") setDraft({ ...draft, to: at });
    else {
      const last = draft.points[draft.points.length - 1];
      if (Math.hypot(at[0] - last[0], at[1] - last[1]) > 0.003) setDraft({ ...draft, points: [...draft.points, at] });
    }
  };

  const onUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    const g = gesture.current;
    gesture.current = null;
    if (g?.kind === "aim") {
      if (g.moved) {
        setPast((p) => [...p.slice(-49), g.before]);
        setFuture([]);
        touch();
      }
      return;
    }
    if (g?.kind === "move") {
      if (g.moved) {
        setPast((p) => [...p.slice(-49), g.before]);
        setFuture([]);
        touch();
      }
      return;
    }
    if (g?.kind === "erase") {
      if (g.erased.size) {
        setPast((p) => [...p.slice(-49), g.before]);
        setFuture([]);
        touch();
        if (selected && g.erased.has(selected)) setSelected(null);
      }
      return;
    }
    if (draft) {
      setDraft(null);
      if (draft.kind === "arrow") {
        const to = frac(e);
        if (Math.hypot(to[0] - draft.from[0], to[1] - draft.from[1]) > 0.012) addItem({ id: newId(), kind: "arrow", color, width, from: draft.from, to, level });
      } else if (draft.points.length > 1) {
        addItem({ id: newId(), kind: "pen", color, width, points: thin(draft.points), level });
      }
    }
  };

  /** A click (no drag) with a placing tool. */
  const onClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (skipClick.current) {
      skipClick.current = false;
      e.stopPropagation();
      return;
    }
    if (!editable) return;
    const [u, v] = frac(e);
    if (tool === "token") {
      e.stopPropagation();
      const used = new Set(phase.items.filter((x) => x.kind === "token" && x.side === tokenSide && x.level === level).map((x) => (x.kind === "token" ? x.n : 0)));
      const n = [1, 2, 3, 4, 5].find((k) => !used.has(k));
      if (!n) return setNote(t("strat.fiveTokens", { side: t(`sideShort.${tokenSide}`) }));
      const id = newId();
      addItem({ id, kind: "token", side: tokenSide, n, u, v, level, look: null, view: true });
      // Selected, so its aim handle is there to drag.
      if (phase.items.length < MAX_STRAT_ITEMS) setSelected(id);
    } else if (isGrenadeTool(tool)) {
      e.stopPropagation();
      let best: { s: Stuff; p: { u: number; v: number }; d: number } | null = null;
      for (const l of landings) {
        const d = Math.hypot(l.p.u - u, l.p.v - v);
        if (d <= STRAT_SNAP && (!best || d < best.d)) best = { ...l, d };
      }
      addItem(
        best
          ? { id: newId(), kind: "grenade", grenade: tool, u: r4(best.p.u), v: r4(best.p.v), stuffId: best.s.id, level }
          : { id: newId(), kind: "grenade", grenade: tool, u, v, stuffId: null, level },
      );
      if (best) setNote(t("strat.linked", { title: best.s.title }));
    } else if (tool === "text") {
      e.stopPropagation();
      setTextDraft({ u, v, value: "", editing: null });
    }
  };

  const commitText = () => {
    const d = textDraft;
    setTextDraft(null);
    if (!d) return;
    const value = d.value.trim().slice(0, 80);
    if (d.editing) {
      if (!value) return removeItem(d.editing);
      return setItems((xs) => xs.map((x) => (x.id === d.editing && x.kind === "text" ? { ...x, text: value } : x)));
    }
    if (value) addItem({ id: newId(), kind: "text", color, u: d.u, v: d.v, text: value, level });
  };

  // ---- Phases

  const addPhase = (copy: boolean) => {
    if (phases.length >= MAX_STRAT_PHASES) return;
    const next: StratPhase = {
      id: newId(),
      name: t("strat.phaseN", { n: phases.length + 1 }),
      items: copy ? phase.items.map((x) => ({ ...x, id: newId() })) : [],
    };
    commitPhases([...phases.slice(0, pi + 1), next, ...phases.slice(pi + 1)]);
    setPi(pi + 1);
    setSelected(null);
  };
  const removePhase = () => {
    if (phases.length <= 1) return;
    if (phase.items.length && !window.confirm(t("strat.confirmDeletePhase", { name: phase.name }))) return;
    commitPhases(phases.filter((_, i) => i !== pi));
    setPi(Math.max(0, pi - 1));
  };
  const renamePhase = (name: string) => {
    const clean = name.trim().slice(0, 30);
    setRenaming(false);
    if (!clean || clean === phase.name) return;
    commitPhases(phases.map((p, i) => (i === pi ? { ...p, name: clean } : p)));
  };
  const movePhase = (dir: -1 | 1) => {
    const j = pi + dir;
    if (j < 0 || j >= phases.length) return;
    const next = [...phases];
    [next[pi], next[j]] = [next[j], next[pi]];
    commitPhases(next);
    setPi(j);
  };

  const closePresenter = useCallback((i: number) => {
    setPresenting(false);
    setPi(i);
  }, []);

  const exportPng = async () => {
    if (!svgRef.current) return;
    try {
      const name = `${doc.title || "strat"}-${phase.name}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      await exportBoardPng(svgRef.current, radarUrl(map, level), `${info.name} · ${doc.title} · ${phase.name}`, `${name}.png`);
    } catch {
      setNote(t("strat.exportFailed"));
    }
  };

  useEffect(() => {
    if (!note) return;
    const timer = setTimeout(() => setNote(null), 3500);
    return () => clearTimeout(timer);
  }, [note]);

  const tools: { key: Tool; label: string; icon: React.ReactNode }[] = [
    { key: "select", label: t("strat.tool.select"), icon: <Icon name="select" /> },
    { key: "token", label: t("strat.tool.token"), icon: <span className="tool-token" style={{ background: SIDE_COLOR[tokenSide] }} /> },
    { key: "arrow", label: t("strat.tool.arrow"), icon: <Icon name="arrow" /> },
    { key: "pen", label: t("strat.tool.pen"), icon: <Icon name="pen" /> },
    { key: "text", label: t("strat.tool.text"), icon: <Icon name="text" /> },
    ...GRENADE_TOOLS.map((g) => ({ key: g as Tool, label: t(`grenade.${g}`), icon: <GrenadeGlyph grenade={g} size={14} /> })),
    { key: "eraser", label: t("strat.tool.eraser"), icon: <Icon name="eraser" /> },
  ];

  const stage = (
    <StratStage
      map={map}
      level={level}
      phases={phases}
      pi={pi}
      items={items}
      selected={selected}
      linked={linked}
      svgRef={svgRef}
      cursor={!editable ? "default" : tool}
      onDown={onDown}
      onMove={onMove}
      onUp={onUp}
      onClick={onClick}
      onDoubleClick={(e) => {
        if (!editable) return;
        const id = itemIdAt(e);
        const it = phase.items.find((x) => x.id === id);
        if (it?.kind === "text") setTextDraft({ u: it.u, v: it.v, value: it.text, editing: it.id });
      }}
      draft={draft && <DraftSvg draft={draft} color={color} width={width} />}
      showViews={showViews}
      aimId={editable && selectedItem?.kind === "token" ? selectedItem.id : null}
    >
      {landings.map(({ s, p }) => (
        <AtPoint key={s.id} u={p.u} v={p.v}>
          <span className="strat-landing-hint" style={{ borderColor: GRENADE_COLOR[s.grenade] }} title={s.title} aria-hidden="true" />
        </AtPoint>
      ))}
      {textDraft && (
        <AtPoint u={textDraft.u} v={textDraft.v}>
          <input
            className="strat-text-input"
            autoFocus
            maxLength={80}
            value={textDraft.value}
            aria-label={t("strat.textLabel")}
            placeholder={t("strat.textPlaceholder")}
            onChange={(e) => setTextDraft({ ...textDraft, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitText();
              if (e.key === "Escape") setTextDraft(null);
            }}
            onBlur={commitText}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        </AtPoint>
      )}
    </StratStage>
  );

  const saveLabel: Record<SaveState, string> = {
    saved: t("strat.save.saved"),
    dirty: t("strat.save.dirty"),
    saving: t("strat.save.saving"),
    error: t("strat.save.error"),
    conflict: t("strat.save.conflict"),
    invalid: t("strat.save.invalid"),
  };

  return (
    <div className="strat-editor">
      <div className="strat-head">
        {editable ? (
          <input
            className="strat-title-input"
            value={doc.title}
            maxLength={60}
            aria-label={t("strat.titleLabel")}
            onChange={(e) => {
              setDoc({ ...doc, title: e.target.value });
              touch();
            }}
          />
        ) : (
          <h2 className="panel-title">{doc.title}</h2>
        )}
        {editable ? (
          <div className="segmented segmented-small" role="group" aria-label={t("form.side")}>
            {(["t", "ct"] as const).map((s) => (
              <button
                key={s}
                type="button"
                className="seg"
                aria-pressed={doc.side === s}
                onClick={() => {
                  setDoc({ ...doc, side: s });
                  setTokenSide(s);
                  touch();
                }}
              >
                {t(`sideShort.${s}`)}
              </button>
            ))}
          </div>
        ) : (
          <span className={`side-tag side-${doc.side}`}>{t(`sideShort.${doc.side}`)}</span>
        )}
        <span className={`strat-save small is-${state}`} role="status">
          {editable ? saveLabel[state] : t("strat.readOnly", { name: strat.author.name })}
        </span>
        <div className="strat-head-actions">
          <ViewLinesToggle value={showViews} onChange={setShowViews} />
          <button type="button" className="btn btn-secondary btn-small" onClick={() => setPresenting(true)}>
            <Icon name="present" size={14} />
            {t("strat.present")}
          </button>
          <button type="button" className="btn btn-quiet btn-small" onClick={() => void exportPng()}>
            <Icon name="download" size={14} />
            {t("strat.export")}
          </button>
        </div>
      </div>
      {state === "conflict" && (
        <div className="banner banner-warning strat-conflict">
          <span>{t("strat.conflict")}</span>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => void loadTheirs()}>
            {t("strat.loadTheirs")}
          </button>
          <button type="button" className="btn btn-quiet btn-small" onClick={() => void keepMine()}>
            {t("strat.keepMine")}
          </button>
        </div>
      )}
      {state === "error" && (
        <div className="banner banner-warning strat-conflict">
          <span>{t("strat.saveFailed")}</span>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => doSave()}>
            {t("common.retry")}
          </button>
        </div>
      )}

      <div className="strat-phases" role="tablist" aria-label={t("strat.phases")}>
        {phases.map((p, i) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            className="strat-phase-tab"
            aria-selected={i === pi}
            onClick={() => {
              setPi(i);
              setSelected(null);
              setRenaming(false);
            }}
          >
            <span className="strat-phase-n">{i + 1}</span>
            {p.name}
          </button>
        ))}
        {editable && phases.length < MAX_STRAT_PHASES && (
          <>
            <button type="button" className="btn btn-quiet btn-small" onClick={() => addPhase(false)}>
              <Icon name="plus" size={14} />
              {t("strat.addPhase")}
            </button>
            <button type="button" className="btn btn-quiet btn-small" onClick={() => addPhase(true)} title={t("strat.copyPhaseHelp")}>
              <Icon name="copy" size={14} />
              {t("strat.copyPhase")}
            </button>
          </>
        )}
      </div>

      <div className="strat-body">
        {editable && (
          <div className="strat-tools" role="toolbar" aria-label={t("strat.tools")} aria-orientation="vertical">
            {tools.map((x) => (
              <button
                key={x.key}
                type="button"
                className="strat-tool"
                aria-pressed={tool === x.key}
                aria-label={x.label}
                title={x.label}
                onClick={() => {
                  setTool(x.key);
                  setDraft(null);
                  if (x.key !== "select") setSelected(null);
                }}
              >
                {x.icon}
              </button>
            ))}
            <span className="strat-tools-sep" aria-hidden="true" />
            <button type="button" className="strat-tool" aria-label={t("strat.undo")} title={t("strat.undo")} disabled={!past.length} onClick={undo}>
              <Icon name="undo" />
            </button>
            <button type="button" className="strat-tool" aria-label={t("strat.redo")} title={t("strat.redo")} disabled={!future.length} onClick={redo}>
              <Icon name="redo" />
            </button>
          </div>
        )}
        <div className="strat-stage-wrap">
          {stage}
          {note && (
            <p className="strat-note small" role="status">
              {note}
            </p>
          )}
        </div>
        <aside className="strat-side card">
          {editable && tool === "token" && (
            <section>
              <h3 className="eyebrow">{t("strat.tokenSide")}</h3>
              <div className="segmented segmented-small" role="group" aria-label={t("strat.tokenSide")}>
                {(["t", "ct"] as const).map((s) => (
                  <button key={s} type="button" className="seg" aria-pressed={tokenSide === s} onClick={() => setTokenSide(s)}>
                    <span className="tool-token" style={{ background: SIDE_COLOR[s] }} aria-hidden="true" />
                    {t(`sideShort.${s}`)}
                  </button>
                ))}
              </div>
              <p className="muted small">{t("strat.tokenHelp")}</p>
            </section>
          )}
          {editable && (tool === "arrow" || tool === "pen" || tool === "text") && (
            <section>
              <h3 className="eyebrow">{t("strat.color")}</h3>
              <ColorPicker value={color} onChange={setColor} />
              {tool !== "text" && (
                <>
                  <h3 className="eyebrow">{t("strat.width")}</h3>
                  <WidthPicker value={width} color={color} onChange={setWidth} />
                </>
              )}
              <p className="muted small">{t(`strat.help.${tool}`)}</p>
            </section>
          )}
          {editable && isGrenadeTool(tool) && <p className="muted small">{t("strat.grenadeHelp", { count: landings.length, grenade: t(`grenade.${tool}`) })}</p>}
          {editable && tool === "eraser" && <p className="muted small">{t("strat.help.eraser")}</p>}
          {editable && tool === "select" && !selectedItem && <p className="muted small">{t("strat.help.select")}</p>}
          {selectedItem && (
            <section className="strat-selection">
              <h3 className="eyebrow">{t(`strat.kind.${selectedItem.kind}`)}</h3>
              {selectedItem.kind === "grenade" &&
                (selectedItem.stuffId && linked.get(selectedItem.stuffId) ? (
                  <p className="row small">
                    <GrenadeGlyph grenade={selectedItem.grenade} size={14} />
                    <StuffLink id={selectedItem.stuffId}>{linked.get(selectedItem.stuffId)!.title}</StuffLink>
                  </p>
                ) : (
                  <p className="muted small">{t("strat.notLinked")}</p>
                ))}
              {selectedItem.kind === "text" && <p className="small">“{selectedItem.text}”</p>}
              {editable && (selectedItem.kind === "arrow" || selectedItem.kind === "pen" || selectedItem.kind === "text") && (
                <ColorPicker value={selectedItem.color} onChange={(c) => setItems((xs) => xs.map((x) => (x.id === selectedItem.id && "color" in x ? { ...x, color: c } : x)))} />
              )}
              {editable && (selectedItem.kind === "arrow" || selectedItem.kind === "pen") && (
                <WidthPicker
                  value={widthOf(selectedItem)}
                  color={selectedItem.color}
                  onChange={(w) => setItems((xs) => xs.map((x) => (x.id === selectedItem.id && (x.kind === "arrow" || x.kind === "pen") ? { ...x, width: w } : x)))}
                />
              )}
              {selectedItem.kind === "token" && (
                <div className="strat-aim-panel">
                  <p className="muted small">{selectedItem.look ? t("strat.aimSet") : t("strat.aimHelp")}</p>
                  {editable && selectedItem.look && (
                    <>
                      <label className="check small">
                        <input
                          type="checkbox"
                          checked={selectedItem.view !== false}
                          onChange={(e) => setItems((xs) => xs.map((x) => (x.id === selectedItem.id && x.kind === "token" ? { ...x, view: e.target.checked } : x)))}
                        />
                        {t("strat.showView")}
                      </label>
                      <button type="button" className="link-button small" onClick={() => setItems((xs) => xs.map((x) => (x.id === selectedItem.id && x.kind === "token" ? { ...x, look: null } : x)))}>
                        {t("strat.removeAim")}
                      </button>
                    </>
                  )}
                </div>
              )}
              {editable && (
                <div className="row">
                  {selectedItem.kind === "text" && (
                    <button type="button" className="btn btn-secondary btn-small" onClick={() => setTextDraft({ u: selectedItem.u, v: selectedItem.v, value: selectedItem.text, editing: selectedItem.id })}>
                      {t("strat.editText")}
                    </button>
                  )}
                  <button type="button" className="btn btn-quiet btn-small btn-danger" onClick={() => removeItem(selectedItem.id)}>
                    <Icon name="trash" size={14} />
                    {t("strat.remove")}
                  </button>
                </div>
              )}
            </section>
          )}

          <section className="strat-phase-panel">
            <h3 className="eyebrow">{t("strat.thisPhase")}</h3>
            {editable && renaming ? (
              <input
                autoFocus
                defaultValue={phase.name}
                maxLength={30}
                aria-label={t("strat.phaseName")}
                onBlur={(e) => renamePhase(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") renamePhase((e.target as HTMLInputElement).value);
                  if (e.key === "Escape") setRenaming(false);
                }}
              />
            ) : (
              <p className="strat-phase-name">{phase.name}</p>
            )}
            <p className="muted small">{t("strat.phaseItems", { count: phase.items.length })}</p>
            {editable && (
              <div className="row">
                <button type="button" className="btn btn-quiet btn-small" onClick={() => setRenaming(true)}>
                  {t("strat.rename")}
                </button>
                <button type="button" className="btn btn-quiet btn-icon" aria-label={t("strat.phaseEarlier")} disabled={pi === 0} onClick={() => movePhase(-1)}>
                  <Icon name="chevronLeft" size={14} />
                </button>
                <button type="button" className="btn btn-quiet btn-icon" aria-label={t("strat.phaseLater")} disabled={pi === phases.length - 1} onClick={() => movePhase(1)}>
                  <Icon name="chevronRight" size={14} />
                </button>
                {phases.length > 1 && (
                  <button type="button" className="btn btn-quiet btn-small btn-danger" onClick={removePhase}>
                    {t("strat.deletePhase")}
                  </button>
                )}
              </div>
            )}
            {pi > 0 && <p className="muted small">{t("strat.earlierFaded")}</p>}
          </section>

          {hasLowerLevel(map) && (
            <div className="segmented segmented-small" role="group" aria-label={t("level.label")}>
              {(["upper", "lower"] as const).map((l) => (
                <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                  {t(`level.${l}`)}
                </button>
              ))}
            </div>
          )}

          {editable ? (
            <label className="field">
              <span>{t("form.notes")}</span>
              <textarea
                rows={3}
                maxLength={1000}
                value={doc.notes}
                placeholder={t("strat.notesPlaceholder")}
                onChange={(e) => {
                  setDoc({ ...doc, notes: e.target.value });
                  touch();
                }}
              />
            </label>
          ) : (
            doc.notes && <p className="notes small">{doc.notes}</p>
          )}
          {!docValid && <p className="field-error small">{t("strat.titleShort")}</p>}
        </aside>
      </div>
      {presenting && <Presenter map={map} title={doc.title} phases={phases} start={pi} linked={linked} onClose={closePresenter} />}
    </div>
  );
}

function ColorPicker({ value, onChange }: { value: StratColor; onChange: (c: StratColor) => void }) {
  const { t } = useTranslation();
  return (
    <div className="strat-colors" role="group" aria-label={t("strat.color")}>
      {STRAT_COLORS.map((c) => (
        <button key={c} type="button" className="strat-color" aria-pressed={value === c} aria-label={t(`strat.colors.${c}`)} title={t(`strat.colors.${c}`)} onClick={() => onChange(c)}>
          <span style={{ background: STRAT_COLOR[c] }} />
        </button>
      ))}
    </div>
  );
}

function WidthPicker({ value, color, onChange }: { value: StratWidth; color: StratColor; onChange: (w: StratWidth) => void }) {
  const { t } = useTranslation();
  return (
    <div className="strat-widths" role="group" aria-label={t("strat.width")}>
      {STRAT_WIDTHS.map((w) => (
        <button key={w} type="button" className="strat-width" aria-pressed={value === w} aria-label={t(`strat.widths.${w}`)} title={t(`strat.widths.${w}`)} onClick={() => onChange(w)}>
          <svg viewBox="0 0 40 12" width="40" height="12" aria-hidden="true">
            <line x1="4" y1="6" x2="36" y2="6" stroke={STRAT_COLOR[color]} strokeWidth={STRAT_WIDTH[w] * 0.9} strokeLinecap="round" />
          </svg>
        </button>
      ))}
    </div>
  );
}

const LAYER: Record<StratItem["kind"], number> = { pen: 0, arrow: 1, grenade: 2, text: 3, token: 4 };
const byLayer = (items: StratItem[]) => [...items].sort((a, b) => LAYER[a.kind] - LAYER[b.kind]);

/**
 * The radar with the board over it: earlier phases faded under the current one.
 * Drawing tools keep the pointer; with nothing to grab, the radar pans and zooms.
 */
function StratStage({
  map,
  level,
  phases,
  pi,
  items,
  selected,
  linked,
  svgRef,
  cursor,
  onDown,
  onMove,
  onUp,
  onClick,
  onDoubleClick,
  draft,
  showViews = true,
  aimId = null,
  children,
}: {
  map: MapId;
  level: Level;
  phases: StratPhase[];
  pi: number;
  items: StratItem[];
  selected: string | null;
  linked: Map<string, Stuff>;
  svgRef?: React.RefObject<SVGSVGElement | null>;
  cursor: Tool | "default";
  onDown?: (e: ReactPointerEvent<SVGSVGElement>) => void;
  onMove?: (e: ReactPointerEvent<SVGSVGElement>) => void;
  onUp?: (e: ReactPointerEvent<SVGSVGElement>) => void;
  onClick?: (e: React.MouseEvent<SVGSVGElement>) => void;
  onDoubleClick?: (e: React.MouseEvent<SVGSVGElement>) => void;
  draft?: React.ReactNode;
  showViews?: boolean;
  /** The player whose aim handle is shown. */
  aimId?: string | null;
  children?: React.ReactNode;
}) {
  const info = mapInfo(map);
  // Lines under markers, players on top, whatever order they were drawn in.
  const earlier = byLayer(phases.slice(0, pi).flatMap((p) => p.items.filter((i) => i.level === level)));
  const current = byLayer(items);
  return (
    <div className="map-frame strat-stage">
      <Radar map={map} mapName={info.name} level={level}>
        <svg
          ref={svgRef}
          className={`strat-svg tool-${cursor}`}
          viewBox={`0 0 ${S} ${S}`}
          preserveAspectRatio="none"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
        >
          <ZoomedOut>
            {(k) => (
              <>
                <g opacity={0.28} className="strat-earlier">
                  {earlier.map((it) => (
                    <StratItemSvg key={`e-${it.id}`} item={it} k={k} showView={showViews} linked={it.kind === "grenade" && !!it.stuffId && linked.has(it.stuffId)} />
                  ))}
                </g>
                <g className="strat-current">
                  {current.map((it) => (
                    <StratItemSvg
                      key={it.id}
                      item={it}
                      k={k}
                      selected={it.id === selected}
                      showView={showViews}
                      aim={it.id === aimId}
                      linked={it.kind === "grenade" && !!it.stuffId && linked.has(it.stuffId)}
                    />
                  ))}
                </g>
              </>
            )}
          </ZoomedOut>
          {draft}
        </svg>
        {children}
      </Radar>
    </div>
  );
}

/** A player's disc on screen, in pixels: the same on a small board, in Present and zoomed in. */
const TOKEN_PX = 9;

/** How much to scale players and markers for them to keep one size on screen, whatever the board's size and zoom. */
function ZoomedOut({ children }: { children: (k: number) => React.ReactNode }) {
  const view = useRadarView();
  const units = (TOKEN_PX * S) / ((view.width || 600) * Math.max(1, view.scale));
  return <>{children(units / TOKEN_R)}</>;
}

/** Full screen, one phase at a time: arrows or the buttons to step through. */
function Presenter({
  map,
  title,
  phases,
  start,
  linked,
  onClose,
}: {
  map: MapId;
  title: string;
  phases: StratPhase[];
  start: number;
  linked: Map<string, Stuff>;
  onClose: (phase: number) => void;
}) {
  const { t } = useTranslation();
  const [i, setI] = useState(start);
  const [level, setLevel] = useState<Level>("upper");
  const [showViews, setShowViews] = useShowViews();
  const box = useRef<HTMLDivElement>(null);
  const iRef = useRef(i);
  iRef.current = i;

  useEffect(() => {
    const el = box.current;
    el?.requestFullscreen?.().catch(() => {});
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") setI((n) => Math.min(phases.length - 1, n + 1));
      else if (e.key === "ArrowLeft" || e.key === "PageUp") setI((n) => Math.max(0, n - 1));
      else if (e.key === "Escape") onClose(iRef.current);
    };
    const onFs = () => {
      if (!document.fullscreenElement) onClose(iRef.current);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    };
  }, [phases.length, onClose]);

  const phase = phases[i];
  return (
    <div className="strat-present" ref={box} role="dialog" aria-modal="true" aria-label={t("strat.presenting", { title })}>
      <div className="strat-present-bar">
        <strong>{title}</strong>
        <span className="strat-present-phase">
          {i + 1} / {phases.length} · {phase.name}
        </span>
        {hasLowerLevel(map) && (
          <div className="segmented segmented-small" role="group" aria-label={t("level.label")}>
            {(["upper", "lower"] as const).map((l) => (
              <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                {t(`level.${l}`)}
              </button>
            ))}
          </div>
        )}
        <div className="row">
          <ViewLinesToggle value={showViews} onChange={setShowViews} />
          <button type="button" className="btn btn-secondary btn-small" disabled={i === 0} onClick={() => setI(i - 1)}>
            <Icon name="chevronLeft" size={14} />
            {t("strat.previous")}
          </button>
          <button type="button" className="btn btn-primary btn-small" disabled={i === phases.length - 1} onClick={() => setI(i + 1)}>
            {t("strat.next")}
            <Icon name="chevronRight" size={14} />
          </button>
          <button type="button" className="btn btn-quiet btn-small" onClick={() => onClose(i)}>
            {t("strat.exitPresent")}
          </button>
        </div>
      </div>
      <div className="strat-present-stage">
        <StratStage map={map} level={level} phases={phases} pi={i} items={phase.items.filter((x) => x.level === level)} selected={null} linked={linked} cursor="default" showViews={showViews} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- The page

function StratBoard({ id, map, stuffs }: { id: string; map: MapId; stuffs: Stuff[] }) {
  const { t } = useTranslation();
  const q = useStrat(id);
  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorState message={errorText(t, q.error)} onRetry={() => void q.refetch()} />;
  return <StratEditor key={q.data.id} strat={q.data} map={map} stuffs={stuffs} />;
}

export function StratsPage() {
  const { t } = useTranslation();
  const map = useMapParam();
  const { scope } = useCurrentTeam();
  const list = useStrats(scope, map ?? "");
  const stuffs = useStuffs(scope, map ?? "");
  const create = useCreateStrat();
  const del = useDeleteStrat();
  const [params, setParams] = useSearchParams();
  const open = params.get("b");

  if (!map) return <Navigate to="/library" replace />;
  if (list.isPending) return <Loading />;
  if (list.error) return <ErrorState message={errorText(t, list.error)} onRetry={() => void list.refetch()} />;

  const boards = list.data;
  const current = boards.find((b) => b.id === open) ?? null;
  const select = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set("b", id);
        else n.delete("b");
        return n;
      },
      { replace: true },
    );
  const newBoard = (from?: { title: string; side: Side; notes: string; phases: StratPhase[] }) =>
    create.mutate(
      {
        scope,
        map,
        side: from?.side ?? "t",
        title: from ? t("strat.copyTitle", { title: from.title }).slice(0, 60) : t("strat.newTitle"),
        notes: from?.notes ?? "",
        phases: from?.phases ?? emptyPhases().map((p) => ({ ...p, name: t("strat.setup") })),
      },
      { onSuccess: (s) => select(s.id) },
    );
  const newButton = (
    <button type="button" className="btn btn-primary" disabled={create.isPending} onClick={() => newBoard()}>
      <Icon name="plus" />
      {t("strat.new")}
    </button>
  );

  return (
    <div className="library">
      <MapBar map={map} section="strats" count={boards.length} countLabel={t("strat.count", { count: boards.length })} actions={newButton} />
      {boards.length === 0 ? (
        <div className="page">
          <div className="card learn-empty">
            <h1 className="section-title">{t("strat.emptyTitle", { map: mapInfo(map).name })}</h1>
            <p className="muted">{t("strat.emptyHelp")}</p>
            {newButton}
            {create.error && <p className="field-error small">{errorText(t, create.error)}</p>}
          </div>
        </div>
      ) : (
        <div className="library-body strat-page">
          <nav className="execution-switcher" aria-label={t("strat.list")}>
            <ul className="execution-list">
              {boards.map((b) => (
                <li key={b.id}>
                  <button type="button" className="execution-item" aria-pressed={current?.id === b.id} onClick={() => select(b.id)}>
                    <span className="execution-item-title">{b.title}</span>
                    <span className="muted small">
                      {t(`sideShort.${b.side}`)} · {t("strat.phasesCount", { count: b.phaseNames.length })} · {b.author.name}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {current && (
              <div className="strat-board-actions">
                <button
                  type="button"
                  className="btn btn-quiet btn-small"
                  disabled={create.isPending}
                  onClick={async () => newBoard(await api<Strat>("GET", `/strats/${current.id}`))}
                >
                  <Icon name="copy" size={14} />
                  {t("strat.duplicate")}
                </button>
                {current.canEdit && (
                  <button
                    type="button"
                    className="btn btn-quiet btn-small btn-danger"
                    disabled={del.isPending}
                    onClick={() => {
                      if (window.confirm(t("strat.confirmDelete", { title: current.title }))) del.mutate(current.id, { onSuccess: () => select(null) });
                    }}
                  >
                    <Icon name="trash" size={14} />
                    {t("strat.delete")}
                  </button>
                )}
              </div>
            )}
            {create.error && <p className="field-error small">{errorText(t, create.error)}</p>}
          </nav>
          <div className="strat-main">
            {current ? (
              <StratBoard id={current.id} map={map} stuffs={stuffs.data ?? []} />
            ) : (
              <div className="card">
                <p className="muted">{t("strat.pick")}</p>
                <p className="small">
                  <Link to={`/library/${map}/executions`}>{t("strat.executionsLink")}</Link>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
